import { useCallback, useMemo } from "react";

import {
  CreativeMetricType,
  MetricFormatters,
  type SalesPageMetric,
  type UpdateCustomMetricVariables,
} from "@north-beam/nb-client-side";
import {
  AccountingModes,
  metrics,
  type AccountingMode,
} from "@north-beam/nb-common";

import { useSelectedMetricId } from "../atoms";

import { useCustomMetricsContext } from "./custom-metrics-provider";
import { useCurrentlyUpdatingMetric } from "./use-currently-updating-metric";

export const useCustomMetrics = (accountingMode?: AccountingMode) => {
  const {
    customMetrics,
    loadingState,
    updateCustomMetric,
    createCustomMetric,
    deleteCustomMetric,
  } = useCustomMetricsContext();

  const [selectedMetricId, setSelectedMetricId] = useSelectedMetricId();
  const clearSelectedMetric = useCallback(
    () => setSelectedMetricId(null),
    [setSelectedMetricId],
  );
  const {
    currentlyUpdatingMetric,
    resetCurrentlyUpdatingMetric,
    createCurrentlyUpdatingMetric,
  } = useCurrentlyUpdatingMetric();

  const selectedMetric = useMemo(
    () => customMetrics.find(({ id }) => id === selectedMetricId) ?? null,
    [customMetrics, selectedMetricId],
  );
  const setSelectedMetric = useCallback(
    (selectedId: number) => {
      setSelectedMetricId(selectedId);
      createCurrentlyUpdatingMetric(
        customMetrics.find(({ id }) => id === selectedId),
      );
    },
    [createCurrentlyUpdatingMetric, customMetrics, setSelectedMetricId],
  );

  const saveMetric = useCallback(
    async (formula: metrics.AstNode) => {
      if (currentlyUpdatingMetric) {
        const variables = {
          ...currentlyUpdatingMetric,
          formula,
          data_type:
            currentlyUpdatingMetric.data_type as UpdateCustomMetricVariables["data_type"],
        };
        const id: number | undefined = currentlyUpdatingMetric.id;
        if (id) {
          const updated = await updateCustomMetric({
            variables: {
              ...variables,
              id,
            },
          });
          const metric = updated.data?.updateCustomMetric;
          if (metric) {
            setSelectedMetricId(metric.id);
            createCurrentlyUpdatingMetric({ ...metric, isTemplate: false });
          }
        } else {
          const created = await createCustomMetric({
            variables: {
              ...currentlyUpdatingMetric,
              formula,
              data_type:
                currentlyUpdatingMetric.data_type as UpdateCustomMetricVariables["data_type"],
            },
          });
          const metric = created.data?.createCustomMetric;
          if (metric) {
            setSelectedMetricId(metric.id);
            createCurrentlyUpdatingMetric({ ...metric, isTemplate: false });
          }
        }
      }
    },
    [
      createCurrentlyUpdatingMetric,
      setSelectedMetricId,
      createCustomMetric,
      currentlyUpdatingMetric,
      updateCustomMetric,
    ],
  );

  const deleteMetric = useCallback(async () => {
    if (currentlyUpdatingMetric?.id) {
      await deleteCustomMetric({
        variables: {
          id: currentlyUpdatingMetric.id,
        },
      });
      // await refetch?.();
      resetCurrentlyUpdatingMetric();
      clearSelectedMetric();
    } else if (currentlyUpdatingMetric?.id === undefined) {
      resetCurrentlyUpdatingMetric();
    }
  }, [
    currentlyUpdatingMetric,
    resetCurrentlyUpdatingMetric,
    deleteCustomMetric,
    clearSelectedMetric,
  ]);

  const customMetricData: SalesPageMetric[] = useMemo(
    () =>
      customMetrics
        .filter(({ accounting_mode }) => {
          if (accountingMode === undefined) {
            return true;
          }
          return accounting_mode === accountingMode;
        })
        .map((customMetric): SalesPageMetric => {
          const formatter =
            customMetric.data_type === "dollars"
              ? MetricFormatters.currency
              : MetricFormatters[customMetric.data_type];

          const formulaEvaluator = new metrics.FormulaEvaluator(
            customMetric.formula,
          );

          const requiredNBCalculatedMetrics =
            formulaEvaluator.getFormulaVariables();

          const id = `{"id":${customMetric.id}}`;

          return {
            id,
            name: customMetric.name,
            format: customMetric.data_type,
            formatter,
            isPositiveChangeGood: !customMetric.is_negative_trend_good,
            requiredNBCalculatedMetrics,
            isRate: false, // ?
            calculate: (variables?: metrics.MetricPoint) => {
              try {
                const evaluator = new metrics.FormulaEvaluator(
                  customMetric.formula,
                );

                const result = evaluator.evaluate(
                  variables as metrics.MetricPointData,
                );

                return result;
              } catch (error) {
                return NaN;
              }
            },
            requiredBaseMetrics: [],
            composition: {
              type: CreativeMetricType.user_defined,
              metric: formulaEvaluator.formula,
              customComposed: true,
            },
            metadata: {
              baseId: id,
              overviewPageMetricId: id,
              dashboardV2Id: id,
              tooltip: {
                reported: "User-defined custom",
                description: customMetric.description,
                // calculationInfo: formulaEvaluator.formula,
              },
            },
            descriptionHTML: customMetric.description,
            spec: {
              id: `customMetric:${customMetric.name}`,
              group: "custom",
              name: customMetric.name,
              modifiers: {
                attributionModel: "all",
                attributionWindows: [],
                accountingModes: [
                  AccountingModes.assert(customMetric.accounting_mode),
                ],
              },
            },
          };
        }),
    [customMetrics, accountingMode],
  );

  return {
    customMetrics,
    customMetricData,
    saveMetric,
    deleteMetric,
    clearSelectedMetric,
    isLoading:
      loadingState.loadingCustomMetrics || loadingState.isDeleteMetricLoading,
    loadingState,

    hasCustomMetrics: customMetrics.length > 0,
    selectedMetricId,
    selectedMetric,
    setSelectedMetric,
  };
};
